/**
 * Derive runtime connector declarations from the platform-materialized
 * `.skaile/assets/connector/<name>/CONNECTOR.md` projection.
 *
 * Connector mirror of MCP's `loadMaterializedMcpDeclarations` /
 * `deriveSingleMaterializedMcpDeclaration`. The generated `CONNECTOR.md` carries
 * a RUNTIME declaration (driver/access/auth/mode/options/mount) — distinct from
 * the catalog `CONNECTOR.md` parsed by `core/manifest.ts:fromConnectorMd`
 * (entry/fields). Driver is referenced by name (first-party; resolved via the
 * connector registry — no code-loading).
 */
import { existsSync, readFileSync, readdirSync, statSync } from "node:fs";
import { join } from "node:path";
import { parseFrontmatter } from "@skaile/workspaces/core";
import type { ConnectorDeclaration } from "./connector-types.js";

/** Parse a generated CONNECTOR.md (runtime projection) into a ConnectorDeclaration. */
export function fromMaterializedConnectorMd(filePath: string, name: string): ConnectorDeclaration {
  const { data: meta, body } = parseFrontmatter(readFileSync(filePath, "utf8"));
  const id = String(meta.name ?? name);
  const decl: ConnectorDeclaration = {
    id,
    driver: String(meta.driver ?? ""),
    access: meta.access === "read-write" ? "read-write" : "read-only",
  };
  if (typeof meta.label === "string" && meta.label) decl.label = meta.label;
  if (typeof meta.auth === "string") decl.auth = meta.auth;
  if (typeof meta.providerLinkId === "string" && meta.providerLinkId) {
    decl.providerLinkId = meta.providerLinkId;
  }
  if (meta.mode === "agent" || meta.mode === "sync") decl.mode = meta.mode;
  if (meta.options && typeof meta.options === "object" && !Array.isArray(meta.options)) {
    decl.options = meta.options as Record<string, unknown>;
  }
  if (meta.mount && typeof meta.mount === "object" && !Array.isArray(meta.mount)) {
    const m = meta.mount as Record<string, unknown>;
    decl.mount = {
      source: String(m.source ?? ""),
      ...(m.target !== undefined ? { target: String(m.target) } : {}),
      ...(typeof m.watch === "boolean" ? { watch: m.watch } : {}),
      ...(typeof m.exposeAccessToken === "boolean"
        ? { exposeAccessToken: m.exposeAccessToken }
        : {}),
      ...(typeof m.accessTokenTTL === "number" ? { accessTokenTTL: m.accessTokenTTL } : {}),
    };
  }
  const trimmedBody = body.trim();
  if (trimmedBody.length > 0) decl.instructions = trimmedBody;
  return decl;
}

/** Provide `.instance.json` config entries as defaults under the declaration's options (the CONNECTOR.md declaration wins on key collision). */
function foldInstanceConfig(decl: ConnectorDeclaration, instancePath: string): void {
  if (!existsSync(instancePath)) return;
  let parsed: { config?: Record<string, unknown> };
  try {
    parsed = JSON.parse(readFileSync(instancePath, "utf8"));
  } catch {
    return;
  }
  const config = parsed.config;
  if (!config || typeof config !== "object" || Array.isArray(config)) return;
  decl.options = { ...(config as Record<string, unknown>), ...(decl.options ?? {}) };
}

/** Derive a single materialized connector declaration; null when no CONNECTOR.md. */
export function deriveSingleMaterializedConnectorDeclaration(
  connectorAssetsDir: string,
  name: string,
): ConnectorDeclaration | null {
  const sub = join(connectorAssetsDir, name);
  const md = join(sub, "CONNECTOR.md");
  if (!existsSync(md)) return null;
  const decl = fromMaterializedConnectorMd(md, name);
  foldInstanceConfig(decl, join(sub, ".instance.json"));
  return decl;
}

/** Scan `<projectDir>/.skaile/assets/connector/*` for materialized connectors. */
export function loadMaterializedConnectorDeclarations(projectDir: string): ConnectorDeclaration[] {
  const root = join(projectDir, ".skaile/assets/connector");
  if (!existsSync(root)) return [];
  const out: ConnectorDeclaration[] = [];
  for (const name of readdirSync(root)) {
    try {
      const sub = join(root, name);
      if (!statSync(sub).isDirectory()) continue;
      const decl = deriveSingleMaterializedConnectorDeclaration(root, name);
      if (decl) out.push(decl);
    } catch {
      // Per-directory failure is isolated — one bad asset never aborts the scan.
    }
  }
  return out;
}
